// Pure helpers for Core Responsibilities. They only describe what the server returned: no score is
// calculated here and no event, person or date is inferred when the record does not hold it.

const time = value => {
    const t = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(t) ? t : null;
};
const latest = values => values.filter(v => time(v) != null).sort((a, b) => time(b) - time(a))[0] || null;

export const monthQuestions = (questions = [], month) => questions.filter(q => Number(q.month) === Number(month));
export const monthsOf = (questions = []) => {
    const set = [...new Set(questions.map(q => Number(q.month)).filter(m => [1, 2, 3].includes(m)))].sort();
    return set.length ? set : [1, 2, 3];
};

// One month of one person: self-assessment progress, submission and the manager's evaluation.
export function summarizeMonth({ month, questions = [], selfAnswers = [], managerAnswers = [], deadlines = [], submittedMonths = [], evaluatedMonths = [], periodOpen = true }) {
    const items = monthQuestions(questions, month);
    const ids = new Set(items.map(q => q.id));
    const self = selfAnswers.filter(a => ids.has(a.questionId));
    const manager = managerAnswers.filter(a => ids.has(a.questionId));
    const rated = items.filter(q => self.some(a => a.questionId === q.id && a.levelId)).length;
    const submitted = submittedMonths.map(Number).includes(Number(month));
    const deadline = deadlines.find(d => Number(d.month) === Number(month)) || null;
    const managerRated = items.filter(q => manager.some(a => a.questionId === q.id)).length;
    const evaluated = evaluatedMonths.map(Number).includes(Number(month)) || (items.length > 0 && managerRated === items.length);

    // In a closed period an unsubmitted month can no longer be completed: it is "not submitted".
    const selfStatus = submitted ? "submitted" : !periodOpen ? "not_submitted" : rated > 0 ? "in_progress" : "not_started";
    const managerStatus = evaluated ? "complete" : managerRated > 0 ? "partial" : "pending";
    const overdue = Boolean(periodOpen && deadline?.overdue && !submitted);
    const result = evaluated ? "evaluated" : submitted ? "submitted" : overdue ? "overdue" : selfStatus;

    return {
        month: Number(month),
        total: items.length,
        rated,
        remaining: Math.max(0, items.length - rated),
        selfStatus,
        submitted,
        submittedAt: submitted ? latest(self.map(a => a.submittedAt)) : null,
        lastSavedAt: latest(self.map(a => a.updatedAt || a.createdAt)),
        dueAt: deadline?.dueAt || null,
        overdue,
        managerStatus,
        managerRated,
        evaluatorIds: [...new Set(manager.map(a => a.evaluatorId).filter(Boolean))],
        evaluatedAt: latest(manager.map(a => a.answeredAt)),
        result,
        editable: Boolean(periodOpen && !submitted && items.length > 0),
    };
}

// Audit rows (RoutineAudit) for the activity log, oldest first, with names only when the viewer's
// directory knows the actor.
export function auditEvents(history = [], nameOf = () => null, currentUserId = null) {
    return [...history]
        .filter(row => row && row.eventType)
        .map(row => ({
            id: row.id,
            type: String(row.eventType),
            month: row.month != null ? Number(row.month) : null,
            questionId: row.questionId || null,
            note: row.note || "",
            at: row.createdAt || null,
            actorId: row.actorId || null,
            actorName: row.actorId ? nameOf(row.actorId) : null,
            isYou: Boolean(currentUserId && row.actorId && String(row.actorId) === String(currentUserId)),
        }))
        .sort((a, b) => (time(a.at) ?? 0) - (time(b.at) ?? 0));
}

export function formatDate(value, language, withTime = false) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat(language === "fr" ? "fr-CA" : "en-CA", {
        year: "numeric", month: "short", day: "numeric", ...(withTime ? { hour: "numeric", minute: "2-digit" } : {}),
    }).format(date);
}
